import { useState } from 'react'
import { Loading, Select } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconFilter, IconFilterFilled, IconPhoto, IconVideo } from '@tabler/icons-react'
import { AlbumFileType } from '@/models/albumFileType.ts'
import { DefaultFileFilter, FileFilterOptions, useFileFilter } from '@/contexts/fileFilter.tsx'
import { useTranslation } from 'react-i18next'

interface FilesFilterProps {
  isLoading?: boolean
  disabled?: boolean
  defaultSort?: string
  isDemo?: boolean
}

export default function FilesFilter({ isLoading, disabled, defaultSort, isDemo }: FilesFilterProps) {
  const [isFiltering, setIsFiltering] = useState(false)
  const [localFilter, setLocalFilter] = useState<FileFilterOptions>({})
  const { fileFilter: globalFileFilter, setFileFilter: setGlobalFileFilter } = useFileFilter()
  const { t } = useTranslation()

  const currentFilterOptions: FileFilterOptions = {
    sort: localFilter.sort || defaultSort as FileFilterOptions['sort'] || DefaultFileFilter.sort,
    media: localFilter.media || DefaultFileFilter.media,
  }

  const isUsingFilter = Object.keys(globalFileFilter || {}).length !== 0

  function onApplyFileFilterClick() {
    setGlobalFileFilter({ ...localFilter })
    setIsFiltering(false)
  }

  function onResetFileFilterClick() {
    setIsFiltering(false)
    setLocalFilter({})
    setGlobalFileFilter(undefined)
  }

  return {
    button: (
      <Button
        className={'rounded-full ' + (isUsingFilter ? '' : 'text-primary')}
        disabled={disabled}
        size='sm'
        color={isUsingFilter ? 'primary' : 'ghost'}
        variant={isUsingFilter ? 'outline' : undefined}
        onClick={() => setIsFiltering(!isFiltering)}
      >
        {isLoading && <Loading color='primary' />}
        {!isLoading && (
          <>
            {!isUsingFilter ? <IconFilter /> : <IconFilterFilled />}
          </>
        )}
        {!isUsingFilter ? t('common.filter') : t('common.filterIsUsed')}
      </Button>
    ),
    panel: isFiltering ? (
      <FilesFilterPanel
        currentFilterOptions={currentFilterOptions}
        localFilter={localFilter}
        setLocalFilter={setLocalFilter}
        isLoading={isLoading}
        isDemo={isDemo}
        onApply={onApplyFileFilterClick}
        onReset={onResetFileFilterClick}
      />
    ) : null,
    isFiltering,
    setIsFiltering,
  }
}

interface FilesFilterPanelProps {
  currentFilterOptions: FileFilterOptions
  localFilter: FileFilterOptions
  setLocalFilter: (filter: FileFilterOptions) => void
  isLoading?: boolean
  isDemo?: boolean
  onApply: () => void
  onReset: () => void
}

export function FilesFilterPanel({
  currentFilterOptions,
  localFilter,
  setLocalFilter,
  isLoading,
  isDemo,
  onApply,
  onReset,
}: FilesFilterPanelProps) {
  const { t } = useTranslation()

  return (
    <div className='flex flex-row gap-3 mx-3 bg-secondary p-3 rounded-lg items-center justify-center'>
      <div className='flex flex-col gap-3'>
        <label className='form-control'>
          <span className="label-text text-primary">{t('common.sorting')}</span>
          {/* @ts-ignore */}
          <Select
            color='primary'
            className='text-primary'
            size='sm'
            value={currentFilterOptions.sort}
            onInput={(e) => setLocalFilter({ ...localFilter, sort: e.currentTarget.value as FileFilterOptions['sort'] })}
          >
            <Select.Option value='-created_at'>
              {t('common.timeOfUpload')} ({t('common.descending')})
            </Select.Option>
            <Select.Option value='created_at'>
              {t('common.timeOfUpload')} ({t('common.ascending')})
            </Select.Option>
            <Select.Option value='-file_creation'>
              {t('common.timeOfCapture')} ({t('common.descending')})
            </Select.Option>
            <Select.Option value='file_creation'>
              {t('common.timeOfCapture')} ({t('common.ascending')})
            </Select.Option>
          </Select>
        </label>

        <label className='form-control'>
          <span className='label-text text-primary'>{t('common.medias')}</span>
          <div className='join w-full flex flex-row'>
            <Button
              className='flex-1 join-item'
              color='primary'
              variant={currentFilterOptions.media === 'all' ? undefined : 'outline'}
              size='sm'
              onClick={() => setLocalFilter({ ...localFilter, media: 'all' })}
            >
              <div className='flex flex-row gap-1'>
                <IconPhoto />
                <IconVideo />
              </div>
            </Button>
            <Button
              className='flex-1 join-item'
              color='primary'
              variant={currentFilterOptions.media === AlbumFileType.IMAGE ? undefined : 'outline'}
              size='sm'
              onClick={() => setLocalFilter({ ...localFilter, media: AlbumFileType.IMAGE })}
            >
              <IconPhoto />
            </Button>
            <Button
              className='flex-1 join-item'
              color='primary'
              variant={currentFilterOptions.media === AlbumFileType.VIDEO ? undefined : 'outline'}
              size='sm'
              onClick={() => setLocalFilter({ ...localFilter, media: AlbumFileType.VIDEO })}
            >
              <IconVideo />
            </Button>
          </div>
        </label>

        <div className='flex flex-row gap-3 w-full mt-2'>
          {isDemo && (
            <p className='text-primary font-bold text-center max-w-[15rem]'>
              {t('common.filterOnlyInFullVersion')}
            </p>
          )}
          {!isDemo && (
            <>
              <Button
                className='rounded-full'
                size='sm'
                color='primary'
                variant='outline'
                onClick={onReset}
              >
                {t('common.resetFilter')}
              </Button>
              <Button
                className='rounded-full flex-1'
                size='sm'
                color='primary'
                onClick={onApply}
                loading={isLoading}
                disabled={isLoading}
              >
                {t('common.applyFilter')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
